import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from '../../commons/useTranslasion';

interface CollapseMenuProps {
  children: React.ReactNode[];
  buttonLabel?: string;
}

const CollapseMenu: React.FC<CollapseMenuProps> = ({ children, buttonLabel }) => {
  const { translate } = useTranslation();
  const [menuExpanded, setMenuExpanded] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  const toggleMenu = () => {
    setMenuExpanded(!menuExpanded);
  };
  
  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setMenuExpanded(false);
    }
  };

  useEffect(() => {
    if (menuExpanded) {
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuExpanded]);
  
  return (
    <Box
      ref={menuRef}
      sx={{
        width: '100%',
        position: 'relative'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
          backgroundColor: '#fff',
          padding: '8px 12px',
          borderRadius: '4px',
          zIndex: 1001,
          position: 'relative'
        }}
      >
        <Tooltip title={menuExpanded ? translate('recolher') : translate('expandir')}>
          <IconButton 
            onClick={toggleMenu} 
            sx={{
              color: 'inherit',
              marginRight: 2,
              backgroundColor: menuExpanded ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
              '&:hover': {
                backgroundColor: menuExpanded ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            {translate('menus')} {menuExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Tooltip>
        
        <Box sx={{ marginLeft: 'auto' }}>
          {React.Children.toArray(children)[0]}
        </Box>
      </Box>
      
      <div style={{ position: 'relative', width: '100%' }}>
        {menuExpanded && (
          <Box
            component="div"
            onClick={(e) => e.stopPropagation()}
            sx={{
              padding: '12px',
              margin: '8px 0 0 0',
              width: '100%',
              backgroundColor: '#fff',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              position: 'absolute',
              zIndex: 1000,
              left: 0,
              top: '100%',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}
          >
            {React.Children.toArray(children)[1]}
          </Box>
        )}
      </div>
    </Box>
  );
};

export default CollapseMenu;
